import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import API, { authFetch } from './api';
import useScoringStatus from './useScoringStatus';
import { ScoringChip, ScoringBanner } from './ScoringStatus';

export default function MatchBoard() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [buyers, setBuyers] = useState([]);
  const [selectedBuyer, setSelectedBuyer] = useState(searchParams.get('buyer') || '');
  const [board, setBoard] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buyerBudget, setBuyerBudget] = useState(null);

  useEffect(() => {
    if (user?.role === 'buyer') {
      setSelectedBuyer(user.buyer_id);
    } else {
      loadBuyers();
    }
  }, []);

  useEffect(() => {
    if (selectedBuyer) loadBoard();
      if (selectedBuyer) loadBuyerBudget(selectedBuyer);
  }, [selectedBuyer]);

  // Background scoring state. While this buyer (or one of the agent's listings) is being
  // scored the board says so; when the queue drains the board reloads itself.
  const { status: scoringStatus, buyerState, propertyState, failedJob, retry: retryScoring } =
    useScoringStatus({ onSettled: () => { if (selectedBuyer) loadBoard(); } });
  const thisBuyerScoring = selectedBuyer ? buyerState(selectedBuyer) : null;

  const loadBuyers = async () => {
    try {
      const res = await authFetch(`${API}/api/buyers`);
      if (res.ok) {
        const data = await res.json();
        setBuyers(data);
        if (!selectedBuyer && data.length > 0) {
          setSelectedBuyer(data[0].id);
        }
      }
    } catch (err) {
      console.error('Load buyers error:', err);
    } finally {
      if (!selectedBuyer) setLoading(false);
    }
  };

  const loadBuyerBudget = async (bid) => {
    try {
      const res = await authFetch(`${API}/api/measurables/definitions/${bid}`);
      if (res.ok) {
        const defs = await res.json();
        const budgetDef = defs.find(d => d.name.toLowerCase().includes('budget') || d.name.toLowerCase().includes('max price'));
        setBuyerBudget(budgetDef?.target_value ? parseFloat(budgetDef.target_value) : null);
      }
    } catch (err) { /* silent */ }
  };

  const loadBoard = async () => {
    setLoading(true);
    try {
      const [boardRes, statsRes] = await Promise.all([
        authFetch(`${API}/api/board/buyer/${selectedBuyer}`),
        authFetch(`${API}/api/board/buyer/${selectedBuyer}/stats`),
      ]);
      if (boardRes.ok) setBoard(await boardRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (err) {
      console.error('Load board error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      'ELITE FIT': 'text-[#00E676] bg-green-900/30 border-green-600/30',
      'STRONG FIT': 'text-[#00D4FF] bg-cyan-900/30 border-cyan-600/30',
      'GOOD FIT': 'text-blue-400 bg-blue-900/30 border-blue-600/30',
      'MARGINAL FIT': 'text-yellow-400 bg-yellow-900/30 border-yellow-600/30',
      'POOR FIT': 'text-red-400 bg-red-900/30 border-red-600/30',
    };
    return colors[tier] || 'text-gray-400 bg-gray-900/30 border-gray-600/30';
  };

  const getPriceColor = (price) => {
    if (!buyerBudget || !price) return 'text-[#00E676]'; // Default green
    const p = parseFloat(price);
    const ratio = p / buyerBudget;
    if (ratio <= 1.0) return 'text-[#00E676]';       // At or under budget — green
    if (ratio <= 1.025) return 'text-yellow-300';     // Up to 2.5% over — yellow
    if (ratio <= 1.05) return 'text-yellow-400';      // Up to 5% over — darker yellow
    if (ratio <= 1.075) return 'text-orange-400';     // Up to 7.5% over — orange
    if (ratio <= 1.10) return 'text-orange-500';      // Up to 10% over — darker orange
    return 'text-red-400';                             // Over 10% — red
  };

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return `$${Number(price).toLocaleString()}`;
  };

  const getScorePercent = (score) => {
    if (!score) return 0;
    return Math.round((score / 9) * 100);
  };

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8 pb-12">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-0 mb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-white">Property Match Board</h1>
            <p className="text-[#B0BEC5] text-sm mt-1">Properties ranked by fit score against buyer priorities</p>
          </div>
          {user?.role !== 'buyer' && buyers.length > 0 && (
            <select
              value={selectedBuyer}
              onChange={(e) => setSelectedBuyer(e.target.value)}
              className="w-full md:w-auto px-4 py-2 bg-[#1A2744] border border-[#546E7A]/30 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-[#00D4FF]"
            >
              <option value="">Select a buyer...</option>
              {buyers.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          )}
        </div>

        {!selectedBuyer ? (
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 text-center py-16">
            <p className="text-[#B0BEC5] text-lg mb-2">Select a buyer to view their match board</p>
            <p className="text-gray-500">Properties are ranked by how well they match each buyer's priorities.</p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-[#00D4FF] text-lg">Loading match board...</div>
          </div>
        ) : (
          <>
            {/* Stats Bar */}
            {stats && stats.total_properties > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-[#1A2744] rounded-lg border border-[#546E7A]/30 p-4 text-center">
                  <div className="text-2xl font-bold text-white">{stats.total_properties}</div>
                  <div className="text-xs text-gray-400">Properties</div>
                </div>
                <div className="bg-[#1A2744] rounded-lg border border-green-600/20 p-4 text-center">
                  <div className="text-2xl font-bold text-[#00E676]">{stats.tier_breakdown?.elite_fit || 0}</div>
                  <div className="text-xs text-gray-400">Elite Fit</div>
                </div>
                <div className="bg-[#1A2744] rounded-lg border border-cyan-600/20 p-4 text-center">
                  <div className="text-2xl font-bold text-[#00D4FF]">{stats.tier_breakdown?.strong_fit || 0}</div>
                  <div className="text-xs text-gray-400">Strong Fit</div>
                </div>
                <div className="bg-[#1A2744] rounded-lg border border-blue-600/20 p-4 text-center">
                  <div className="text-2xl font-bold text-blue-400">{stats.tier_breakdown?.good_fit || 0}</div>
                  <div className="text-xs text-gray-400">Good Fit</div>
                </div>
                <div className="bg-[#1A2744] rounded-lg border border-[#546E7A]/30 p-4 text-center">
                  <div className="text-2xl font-bold text-white">{stats.avg_score || 0}</div>
                  <div className="text-xs text-gray-400">Avg Score</div>
                </div>
              </div>
            )}

            {/* Background scoring — clears itself and reloads the board when the queue drains */}
            <ScoringBanner status={scoringStatus} onRetry={retryScoring} label="clients" />

            {/* Board */}
            {board.length === 0 ? (
              thisBuyerScoring === 'scoring' ? (
                <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 text-center py-16 px-4">
                  <p className="text-[#B0BEC5] text-lg mb-2">Scoring this client's matches…</p>
                  <p className="text-gray-500">This runs in the background. Their ranked properties will appear here automatically.</p>
                </div>
              ) : (
              <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 text-center py-16">
                <p className="text-[#B0BEC5] text-lg mb-2">No evaluated properties yet</p>
                <p className="text-gray-500 mb-4">Grade properties against this buyer's criteria to see them ranked here.</p>
                <Link to="/properties" className="inline-block px-6 py-2 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00B8D4] transition-colors">
                  Go to Properties
                </Link>
              </div>
              )
            ) : (
              <div className="space-y-3">
                {board.map((prop) => (
                  <Link
                    key={prop.property_id}
                    to={`/properties/${prop.property_id}/buyer/${selectedBuyer}`}
                    className="block bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4 md:p-5 hover:border-[#00D4FF]/40 transition-all"
                  >
                    <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                      {/* Rank + Score (mobile: row; desktop: separate cells) */}
                      <div className="flex items-center gap-4 md:contents">
                        <div className="text-center w-12 shrink-0">
                          <div className="text-2xl font-bold text-[#546E7A]">#{prop.rank}</div>
                        </div>

                        {/* Score Circle */}
                        <div className="text-center w-20 shrink-0">
                          <div className="text-2xl md:text-3xl font-bold text-white">{getScorePercent(prop.fit_score)}%</div>
                          <div className={`text-xs font-semibold mt-1 px-2 py-0.5 rounded inline-block ${getTierColor(prop.tier)}`}>
                            {prop.tier}
                          </div>
                        </div>
                      </div>

                      {/* Property Details */}
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-semibold text-base md:text-lg break-words">{prop.address}</div>
                        {propertyState(prop.property_id) && (
                          <div className="mt-1">
                            <ScoringChip
                              state={propertyState(prop.property_id)}
                              failedJob={failedJob('property', prop.property_id)}
                              onRetry={retryScoring}
                            />
                          </div>
                        )}
                        <div className="text-gray-400 text-sm break-words">
                          {prop.city}, {prop.state} {prop.zip}
                          {prop.school_district && <span className="ml-2">· {prop.school_district}</span>}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-400">
                          <span>{prop.beds || '-'} bed</span>
                          <span>{prop.baths || '-'} bath</span>
                          <span>{prop.sqft ? prop.sqft.toLocaleString() : '-'} sqft</span>
                          {prop.lot_size_acres && <span>{prop.lot_size_acres} acres</span>}
                          {prop.year_built && <span>Built {prop.year_built}</span>}
                        </div>
                      </div>

                      {/* Price & MLS Link */}
                      <div className="text-left md:text-right md:shrink-0">
                        <div className={`${getPriceColor(prop.price)} font-bold text-xl`}>{formatPrice(prop.price)}</div>
                        {prop.sqft && prop.price && (
                          <div className="text-gray-500 text-sm">${Math.round(prop.price / prop.sqft)}/sqft</div>
                        )}
                        {prop.listing_url && (
                          <span
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(prop.listing_url, '_blank'); }}
                            className="inline-block mt-1 text-[#00D4FF] hover:text-[#00E676] text-sm cursor-pointer"
                          >
                            View Listing →
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Dimension Scores Bar */}
                    {prop.dimension_scores && Object.keys(prop.dimension_scores).length > 0 && (
                      <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-[#546E7A]/20">
                        {Object.entries(prop.dimension_scores).map(([dim, score]) => (
                          <div key={dim} className="text-center flex-1 min-w-[60px]">
                            <div className="text-xs text-gray-500">{dim}</div>
                            <div className="text-sm font-semibold text-white">{getScorePercent(score)}%</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
